import { useState } from 'react';
import { supabase, extractPathFromUrl as extractPathHelper } from '../../lib/supabase';

export const useImageUpload = (bucket = 'product-images') => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // ... (sanitizeFileName function unchanged)
  const sanitizeFileName = (originalName) => {
    const fileExt = originalName.split('.').pop();
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.'));
    
    const cleanName = nameWithoutExt
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Elimina acentos y tildes
      .replace(/[^a-z0-9]/g, '-')     // Sustituye espacios y símbolos por guiones
      .replace(/-+/g, '-');           // Elimina guiones repetidos

    const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
    return `${cleanName}-${uniqueId}.${fileExt}`;
  };

  const uploadImage = async (file) => {
    try {
      setUploading(true);
      setError(null);

      // Validar tamaño del archivo (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('La imagen no debe superar 5MB');
      }

      // Validar tipo de archivo
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Solo se permiten imágenes JPG, PNG o WEBP');
      }

      // Generar nombre único y amigable para SEO
      const fileName = sanitizeFileName(file.name);

      // Subir archivo a Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Obtener URL pública
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path);

      return {
        path: data.path,
        url: urlData.publicUrl
      };
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (path) => {
    try {
      if (!path) return;

      const { error: deleteError } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (deleteError) throw deleteError;
    } catch (err) {
      console.error('Error deleting image:', err);
      throw err;
    }
  };

  // Helper para extraer el path de una URL usando el bucket de este hook
  const extractPathFromUrl = (url) => {
    return extractPathHelper(url, bucket);
  };

  return {
    uploading,
    error,
    uploadImage,
    deleteImage,
    extractPathFromUrl
  };
};